import React from 'react';
import FilterPill from '../FilterPill/FilterPill';
import './RarityPills.css';

interface RarityPillsProps {
  value: string[]; // Array of selected rarities (lowercase: 'common', 'uncommon', 'rare', 'mythic')
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

const RARITIES = [
  { value: 'common', label: 'Common' },
  { value: 'uncommon', label: 'Uncommon' },
  { value: 'rare', label: 'Rare' },
  { value: 'mythic', label: 'Mythic' },
] as const;

const RarityPills: React.FC<RarityPillsProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  // If no rarities selected, treat as "all selected" for display
  const allSelected = value.length === 0 || value.length === RARITIES.length;

  const handleToggle = (rarity: string) => {
    if (disabled) return;

    // If all are currently selected (value is empty), clicking one means select only that one
    if (value.length === 0) {
      onChange([rarity]);
      return;
    }

    const newValue = value.includes(rarity)
      ? value.filter(v => v !== rarity)
      : [...value, rarity];

    // If all selected, clear the array (backend treats empty array as "all")
    if (newValue.length === RARITIES.length) {
      onChange([]);
      return;
    }

    // If none selected after removal, keep at least one (the one being clicked off)
    if (newValue.length === 0) {
      return;
    }

    onChange(newValue);
  };

  const isActive = (rarity: string): boolean => {
    // If value is empty, all are considered active
    if (value.length === 0) return true;
    return value.includes(rarity);
  };

  return (
    <div className={`rarity-pills ${disabled ? 'disabled' : ''}`}>
      <span className="rarity-pills-label">Rarity:</span>
      <div className="rarity-pills-options">
        {RARITIES.map(rarity => (
          <FilterPill
            key={rarity.value}
            label={rarity.label}
            active={isActive(rarity.value)}
            onToggle={() => handleToggle(rarity.value)}
          />
        ))}
      </div>
      {allSelected && (
        <span className="rarity-pills-hint">(All rarities)</span>
      )}
    </div>
  );
};

export default RarityPills;
